import { Platform } from 'react-native';
import ReactNativeHapticFeedback, { HapticFeedbackTypes } from 'react-native-haptic-feedback';

/** Haptic types used by Clickable/Button and tactile menus. */
export type HapticType =
  | 'impactLight'
  | 'impactMedium'
  | 'impactHeavy'
  | 'notificationSuccess'
  | 'notificationWarning'
  | 'notificationError'
  | 'selection'
  | 'rigid'
  | 'soft';

/** Clickable impact presets — mirrors legacy `expo-haptics` `ImpactFeedbackStyle` values. */
export const ImpactFeedbackStyle = {
  Light: 'impactLight',
  Medium: 'impactMedium',
  Heavy: 'impactHeavy',
  Rigid: 'rigid',
  Soft: 'soft',
} as const;

export type ImpactHapticType = (typeof ImpactFeedbackStyle)[keyof typeof ImpactFeedbackStyle];

const HAPTIC_OPTIONS = {
  enableVibrateFallback: false,
  ignoreAndroidSystemSettings: false,
} as const;

const SELECTION_MIN_GAP_MS = 64;

let selection_last_ms = 0;
let haptics_muted = false;

/** Global intensity 0–1 — scales how often strong patterns fire (selection stays subtle). */
let haptic_intensity = 0.72;

const HAPTIC_TRIGGER_MAP: Record<HapticType, HapticFeedbackTypes> = {
  impactLight: HapticFeedbackTypes.impactLight,
  impactMedium: HapticFeedbackTypes.impactMedium,
  impactHeavy: HapticFeedbackTypes.impactHeavy,
  notificationSuccess: HapticFeedbackTypes.notificationSuccess,
  notificationWarning: HapticFeedbackTypes.notificationWarning,
  notificationError: HapticFeedbackTypes.notificationError,
  selection: HapticFeedbackTypes.selection,
  rigid: HapticFeedbackTypes.rigid,
  soft: HapticFeedbackTypes.soft,
};

function shouldThrottleSelection(type: HapticType): boolean {
  if (type !== 'selection') {
    return false;
  }
  const now = Date.now();
  if (now - selection_last_ms < SELECTION_MIN_GAP_MS) {
    return true;
  }
  selection_last_ms = now;
  return false;
}

function resolveTriggerType(type: HapticType): HapticFeedbackTypes {
  if (haptic_intensity < 0.35 && (type === 'impactHeavy' || type === 'impactMedium')) {
    return HapticFeedbackTypes.soft;
  }
  if (haptic_intensity < 0.55 && type === 'impactHeavy') {
    return HapticFeedbackTypes.impactLight;
  }
  return HAPTIC_TRIGGER_MAP[type] ?? HapticFeedbackTypes.soft;
}

/**
 * Trigger haptic feedback. Use from JS (e.g. in callbacks), not from worklets.
 * Uses `react-native-haptic-feedback` (UIKit / subtle Android effects) — no harsh `Vibration` motor.
 *
 * Android: disabled — the library maps most types to full-strength `VibrationEffect` patterns that feel
 * excessive on typical devices; iOS keeps subtle UIKit feedback.
 */
export function triggerHaptic(type: HapticType): void {
  if (Platform.OS === 'android') {
    return;
  }
  if (haptics_muted) {
    return;
  }
  if (shouldThrottleSelection(type)) {
    return;
  }
  try {
    ReactNativeHapticFeedback.trigger(resolveTriggerType(type), HAPTIC_OPTIONS);
  } catch {
    // Simulator / devices without a vibrator.
  }
}

/** Wheel picker, carousel index ticks, miles chart week changes. */
export function triggerSelectionHaptic(): void {
  triggerHaptic('selection');
}

/** exDev / QA only — temporarily mute all facade haptics. */
export function setHapticsMuted(muted: boolean): void {
  haptics_muted = muted;
}

export function isHapticsMuted(): boolean {
  return haptics_muted;
}

/** Tune global haptic strength (0 = mute motor patterns, 1 = full catalog). */
export function setHapticIntensity(intensity: number): void {
  haptic_intensity = Math.min(1, Math.max(0, intensity));
}

export function getHapticIntensity(): number {
  return haptic_intensity;
}
